import {
  always, assoc, chain, compose, curry, lensProp, map, merge, over, pipe, propEq, values,
  view, when,
} from 'ramda';

import {raise} from '@sinclair-digital/sbg-util/dist/es/exception';
import {maybeFind} from '@sinclair-digital/sbg-util/dist/es/list';
import {keyBy, maybeProp} from '@sinclair-digital/sbg-util/dist/es/object';
import {getFeatureFlag} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/featureFlags';

const myMediaFilterEnabled = () => getFeatureFlag('myMediaFilter');

export const defaultOptions = {
  searchText: {
    groupName: 'Search',
    filterType: 'searchbar',
    filters: [],
    text: '',
  },
  fromDate: null,
  thruDate: null,
  scope: {
    groupName: 'Properties',
    filterType: 'radio',
    filters: [
      {
        name: 'all',
        text: 'all properties',
        value: ['ALL'],
      }, {
        name: 'my',
        text: 'my properties',
        value: [], // populated at runtime
        selected: true,
      },
    ],
  },
  packageType: {
    groupName: 'Type',
    filterType: 'checkbox',
    filters: [
      {
        name: 'all',
        value: ['IMAGE', 'AUDIO', 'VIDEO'],
        text: 'all types',
        selected: true,
        exclusive: true,
        basicFilter: true,
      }, {
        name: 'image',
        value: ['IMAGE'],
        text: 'images',
        basicFilter: true,
      }, {
        name: 'audio',
        value: ['AUDIO'],
        text: 'audio',
      }, {
        name: 'video',
        value: ['VIDEO'],
        text: 'video',
        basicFilter: true,
      }, {
        name: 'broadcast',
        value: ['BROADCAST'],
        text: 'broadcast',
        exclusive: true,
        basicFilter: true,
        channel: null,
        channels: [], // populated at runtime
      },
    ],
  },
  field: {
    groupName: 'Fields',
    filterType: 'checkbox',
    filters: [
      {
        name: 'all',
        value: [],
        text: 'all fields',
        selected: true,
        exclusive: true,
      }, {
        name: 'title',
        value: ['title'],
        text: 'title',
      }, {
        name: 'caption',
        value: ['caption'],
        text: 'caption',
      }, {
        name: 'altText',
        value: ['alt_text'],
        text: 'alt text',
      }, {
        name: 'tags',
        value: ['all_tags'],
        text: 'tags',
      },
    ],
  },
  sortOptions: {
    groupName: 'Sort',
    filterType: 'radio',
    filters: [{
      sortColumn: 'Date',
      text: 'upload date',
      sortDirection: 'descending',
      selected: true,
    }],
  },
};

if (myMediaFilterEnabled()) {
  defaultOptions.myMedia = {
      groupName: 'MyMedia',
      filterType: 'radio',
      filters: [
        {
          name: 'all',
          text: 'all media',
          value: [],
        }, {
          name: 'my',
          text: 'my media',
          value: [], // populated at runtime
          selected: true,
        },
      ],
  };
}

// filtersLens :: Lens FilterGroup
const filtersLens = lensProp('filters');

// isBroadcast :: Filter -> Boolean
const isBroadcast = propEq('name', 'broadcast');

// isSelected :: Filter -> Boolean
const isSelected = propEq('selected', true);

const generateComposedLens = prop => compose(lensProp(prop), filtersLens);

const _groupFiltersLenses = {
  packageType: compose(lensProp('packageType'), filtersLens),
  field: compose(lensProp('field'), filtersLens),
  scope: compose(lensProp('scope'), filtersLens),
  content: compose(lensProp('content'), filtersLens),
  myMedia: compose(lensProp('myMedia'), filtersLens),
  constraint: generateComposedLens('constraint'),
  generic: generateComposedLens('generic'),
  status: generateComposedLens('status'),
  checkbox: generateComposedLens('checkbox'),
};

// getGroupFiltersLens :: String -> Lens SearchOptions | throw
export const getGroupFiltersLens = name =>
  _groupFiltersLenses[name] || raise(new Error(`"${name}" is not a valid filter group`));

// getBroadcastFilter :: SearchOptions -> Maybe Filter
export const getBroadcastFilter = searchOptions =>
  maybeFind(isBroadcast, view(getGroupFiltersLens('packageType'), searchOptions));

// setBroadcastFilter :: (Filter, SearchOptions) -> SearchOptions
export const setBroadcastFilter = (newBroadcastFilter, searchOptions) => {
  const _setBroadcastFilters = map(when(isBroadcast, always(newBroadcastFilter)));
  return over(getGroupFiltersLens('packageType'), _setBroadcastFilters, searchOptions);
};

const keyByName = keyBy('name');

// Merge new filters with old
// mergeFilters :: [Filter] -> [Filter] -> [Filter]
export const mergeFilters = newFilters => oldFilters => {
  const _old = keyByName(oldFilters);
  const _new = keyByName(newFilters);
  return values(merge(_old, _new));
};

// setFilters :: (String, [Filter], SearchOptions) -> SearchOptions
export const setFilters = (groupName, filters, searchOptions) =>
  over(getGroupFiltersLens(groupName), mergeFilters(filters), searchOptions);

// setBroadcastChannelList :: ([Channel], SearchOptions) -> Maybe SearchOptions
export const setBroadcastChannelList = curry((channels, searchOptions) =>
  getBroadcastFilter(searchOptions).map(broadcastFilter =>
    setBroadcastFilter(assoc('channels', channels, broadcastFilter), searchOptions)
  )
);

// getSelectedFilters :: String -> SearchOptions -> [Filter]
export const getSelectedFilters = curry((groupName, searchOptions) =>
  view(getGroupFiltersLens(groupName), searchOptions)
    .filter(isSelected)
);

// :: (String, SearchOptions) -> Maybe SearchOptions
export const setBroadcastChannel = curry((callLetters, searchOptions) =>
  getBroadcastFilter(searchOptions).map(broadcastFilter =>
    setBroadcastFilter(assoc('channel', callLetters, broadcastFilter), searchOptions)
  )
);

// setFilterSelection :: String -> [Filter] -> [Filter]
const setFilterSelection = filterName => map(searchFilter => ({
  ...searchFilter,
  selected: searchFilter.name === filterName,
}));

// removeFilterSelection :: String -> [Filter] -> [Filter]
const removeFilterSelection = filterName => map(searchFilter => ({
  ...searchFilter,
  selected: searchFilter.name === filterName ? false : searchFilter.selected,
}));

// selectFilter :: (String, String, SearchOptions) -> SearchOptions
export const selectFilter = (groupName, name, searchOptions) =>
  over(getGroupFiltersLens(groupName), setFilterSelection(name), searchOptions);

// deselectFilter :: (String, String, SearchOptions) -> SearchOptions
export const deselectFilter = (groupName, name, searchOptions) =>
  over(getGroupFiltersLens(groupName), removeFilterSelection(name), searchOptions);


// getFilterByName :: String -> String -> SearchOptions -> Filter
export const getFilterByName = curry((groupName, name, searchOptions) =>
  view(getGroupFiltersLens(groupName), searchOptions)
    .find(propEq('name', name))
);

// getSelectedBroadcast :: SearchOptions -> Maybe String
export const getSelectedBroadcast = pipe(
  getBroadcastFilter,
  chain(maybeProp('channel'))
);
